/* eslint-disable @typescript-eslint/indent */
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import Counter from '@components/common/Counter';
import ProductData from '@customTypes/product';
import ButtonPrimary from '@components/common/ButtonPrimary';
import { getDateWithDashes, getDetailedDate, parseMilitaryTime } from '@helpers/date';
import Image from 'next/image';
import arrowLeft from '@assets/images/arrow-left.svg';
import closeArrow from '@assets/images/close-arrows.svg';
import Currency from '@components/common/Currrency/Currency';
import {
  getTotalForProductSelector,
  numberOfAvailableUnits,
  showAvailabilityWarning,
} from '@helpers/product';
import { CartItemDetails } from '@customTypes/cart-items';
import inventoryTypes from '@constants/inventoryTypes';
import { CartPayload } from '@customTypes/cart-payload';
import Checkbox from '@components/common/Checkbox/Checkbox';
import { addonSelected } from '@events/DLP';
import HotelDetails from '@customTypes/hotel-details';
import { quantitySelected } from '@events/timeSlottedProduct';
import { store } from '@context/store';
import {
  AMPLITUDE_EXPERIMENT_CONTROL_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME,
} from '@constants/amplitudeExperiments';
import { isToday } from 'date-fns';
import actionTypes from '@context/actionTypes';
import { useRouter } from 'next/router';
import Button from '@solar/common/Button/ButtonV2';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';

type Props = {
  addItemsToCart: (productID: number, addonItems: any) => void;
  adultCounter: number;
  childrenCounter: number;
  date: Date | undefined;
  hotelId: number | undefined;
  hotelDetails: HotelDetails;
  product: ProductData;
  setAdultCounter: Dispatch<SetStateAction<number>>;
  scrollToDateField: () => void;
  setChildrenCounter: Dispatch<SetStateAction<number>>;
  setStep: Dispatch<SetStateAction<number>>;
  setIsSelectorOpen: Dispatch<SetStateAction<boolean>>;
  isTimeSlottedProduct?: boolean;
  cartData?: CartItemDetails;
  showFairPricing: boolean;
};

const defaultProps = {
  isTimeSlottedProduct: false,
  cartData: null,
};

function TimeSlotProductSelector({
  addItemsToCart,
  adultCounter,
  childrenCounter,
  date,
  hotelId,
  hotelDetails,
  isTimeSlottedProduct,
  product,
  setAdultCounter,
  setChildrenCounter,
  setStep,
  scrollToDateField,
  setIsSelectorOpen,
  cartData,
  showFairPricing,
}: Props) {
  const [addOnItems, setAddOnItems] = useState<any>([]);
  const router = useRouter();
  const { dispatch } = useContext(store);
  const { ctasUpdateExperiment } = useFeatureFlagContext();

  const prepareJsonData = (): CartPayload => ({
    items: [
      {
        product_id: product.product_id,
        hotel_id: hotelId ?? 0,
        booked_date: date ? getDateWithDashes(date) : '',
        no_of_adults: adultCounter,
        no_of_childs: childrenCounter,
        no_of_infants: 0,
        addons: addOnItems.map((item: any) => {
          if (item.isChecked) {
            return {
              addon_id: +item.id,
              units: item.units,
            };
          }

          return null;
        }),
      },
    ],

    inventory_type: inventoryTypes.RESORT_PASS,
  });

  const resetAddOns = useCallback(() => {
    const addOns: any[] = [];
    product.addons.forEach((item) => {
      if (item.active) {
        addOns.push({
          id: item.id,
          name: item.name,
          isChecked: false,
          price: item.price,
          max: item.max,
          addon_id: item.id,
          units: 1,
          type: item.type,
        });
      }
    });
    setAddOnItems(addOns);
  }, [product.addons]);

  const updateAddonItem = (item: any, value: boolean) => {
    const updatedAddOns = [...addOnItems];
    const currentItem = updatedAddOns.find((addon) => addon.id === item.id);
    if (currentItem) {
      currentItem.isChecked = value;
    }
    setAddOnItems(updatedAddOns);

    // Only fire the addonSelected event when the checkbox is marked.
    if (value) {
      // Event
      addonSelected(item, hotelDetails);
    }
  };

  const onAddonUnitsSelect = (value: string, item: any) => {
    const updatedAddOns = [...addOnItems];
    const currentItem = updatedAddOns.find((addon) => addon.id === item.id);
    if (currentItem) {
      currentItem.units = +value;
    }
    setAddOnItems(updatedAddOns);
  };

  const addToCart = async () => {
    const data = prepareJsonData();

    try {
      if (isTimeSlottedProduct) {
        // Event
        quantitySelected(product, data);
      }

      await addItemsToCart(product.product_id, addOnItems);

      if (date && isToday(date)) {
        dispatch({
          type: actionTypes.OPEN_SAME_DAY_BOOKING_WARNING,
          payload: true,
        });
      } else {
        router.push('/users/guest-billing-detail');
      }
    } catch (e) {
      //
    }
  };

  const areChildrenAllowed = () =>
    product.allow_child || (!product.individual_billing && product.max_children_occupancy > 0);

  useEffect(() => {
    resetAddOns();
  }, [resetAddOns]);

  return (
    <div className="flex flex-col items-center justify-start max-h-full overflow-y-auto rounded-xl w-full">
      <div className="h-74px flex justify-between items-center w-full bg-white z-100 rounded-t-xl">
        {isTimeSlottedProduct ? (
          <button
            type="button"
            className="absolute flex left-26px top-28px"
            onClick={() => setStep(0)}
          >
            <Image src={arrowLeft} width={16} height={16} alt="back to time slot selector" />
          </button>
        ) : (
          <button
            type="button"
            className="absolute flex left-26px top-28px"
            onClick={() => setIsSelectorOpen(false)}
          >
            <Image src={closeArrow} width={16} height={16} alt="close product selector" />
          </button>
        )}

        <div className="border-b flex flex-col items-center justify-center py-4 rounded-t-xl w-full">
          <p className="font-rp-pn-semi-bold leading-19px text-base text-center">
            {isTimeSlottedProduct
              ? `${parseMilitaryTime(product.start_time)} - ${parseMilitaryTime(product.end_time)}`
              : product.name}
          </p>
          {date && <p className="mt-1 leading-17px opacity-70 text-sm">{getDetailedDate(date)}</p>}
        </div>
      </div>

      <div className="d:mb-0 mb-20 overflow-y-scroll w-full d:px-10">
        <p className="font-rp-pn-semi-bold leading-40px mt-30px mx-22px -tracking-0.5  text-2xl text-rp-primary-black-medium d:leading-29px d:mb-18px d:mx-0 d:text-28 d:-tracking-0.24">
          Select guests
        </p>

        <div className="border-rp-gray-divider p-5 w-full d:border d:mb-28 d:rounded-xl">
          <div
            className={`${
              areChildrenAllowed() ? 'pb-5' : 'pb-0'
            } border-t border-rp-gray-divider flex flex-row items-center justify-between pt-4 d:pt-0 d:border-0`}
          >
            <div className="flex flex-col font-rp-pn-semi-bold">
              <p className="leading-21px text-17">Adult</p>
              {product.adult_age_restriction_text && (
                <p className="font-rp-pn-regular leading-6 mt-7px opacity-50 text-17">
                  {product.adult_age_restriction_text}
                </p>
              )}

              {!isTimeSlottedProduct && date && showAvailabilityWarning(product, undefined) && (
                <p className="leading-4 mt-7px text-rp-gold text-13">
                  {`ONLY ${numberOfAvailableUnits(product, undefined)} LEFT`}
                </p>
              )}
            </div>

            <div className="flex items-center space-x-4">
              <Counter
                setParentCounter={setAdultCounter}
                counter={adultCounter}
                productData={product}
                adultCounter={adultCounter}
                childrenCounter={childrenCounter}
                date={date}
                scrollToDateField={scrollToDateField}
                // setIsSelectorOpen={setIsSelectorOpen}
                personType="adults"
                availableUnits={numberOfAvailableUnits(product, cartData)}
                ctasUpdateExperiment={ctasUpdateExperiment}
              />
            </div>
          </div>

          {areChildrenAllowed() && (
            <div className="border-t border-rp-gray-divider flex flex-row items-center justify-between pt-4">
              <div className="flex flex-col font-rp-pn-semi-bold">
                <p className="leading-23px text-17">Children</p>
                {product.adult_age_restriction_text && (
                  <p className="font-rp-pn-regular leading-6 mt-7px opacity-50 text-17">
                    {product.child_age_restriction_text}
                  </p>
                )}
              </div>

              <div className="flex items-center space-x-4">
                <Counter
                  setParentCounter={setChildrenCounter}
                  counter={childrenCounter}
                  productData={product}
                  adultCounter={adultCounter}
                  childrenCounter={childrenCounter}
                  date={date}
                  scrollToDateField={scrollToDateField}
                  // setIsSelectorOpen={setIsSelectorOpen}
                  personType="children"
                  ctasUpdateExperiment={ctasUpdateExperiment}
                />
              </div>
            </div>
          )}

          {addOnItems.length > 0 && (
            <div className="mt-5 pb-2 text-base z-50 d:max-h-64 d:overflow-y-scroll d:pr-2 d:w-full">
              <div className="pt-3 border-t border-rp-gray-divider">
                <p className="text-13 font-rp-pn-semi-bold text-rp-primary-black opacity-50">
                  ADD-ONS
                </p>
                {addOnItems.map((item: any) => (
                  <div key={item.id} className="flex justify-between py-1.5 items-center">
                    <div className="font-rp-pn-semi-bold w-3/4">
                      <Checkbox
                        label={item.name}
                        isChecked={item.isChecked}
                        setIsChecked={(value: boolean) => updateAddonItem(item, value)}
                        id={item.id}
                        secondaryText={`${item.price}`}
                      />
                    </div>
                    {item.addon_type !== 'single' && (
                      <select
                        onChange={
                          (e: React.ChangeEvent<HTMLSelectElement>) =>
                            onAddonUnitsSelect(e.target.value, item)
                          // eslint-disable-next-line react/jsx-curly-newline
                        }
                        value={item.units}
                        className="w-20 h-10 border pl-6 rounded-xl text-base d:text-17 d:w-20 border-rp-gray-divider bg-white shadow-select"
                      >
                        {Array.from(Array(item.max).keys()).map((num) => (
                          <option key={num + 1} value={num + 1}>
                            {num + 1}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="absolute bg-white bottom-0 flex items-center justify-between px-22px py-4 shadow-drawer-footer w-full d:px-10 d:rounded-b-xl">
        <div className="flex flex-col">
          <p className="leading-22px text-15">Total:</p>

          <div className="flex space-x-2">
            <Currency
              value={
                adultCounter > 0 || childrenCounter > 0
                  ? getTotalForProductSelector({
                      product,
                      adultCount: product.individual_billing ? adultCounter : 1,
                      childrenCount: childrenCounter,
                      addOnItems,
                      hotSpot: hotelDetails.hot_spot,
                      applyHotSpot: product.is_hot_spot,
                      applyFairPricing: showFairPricing,
                    })
                  : 0
              }
              valueClasses="leading-6 text-base font-rp-pn-semi-bold"
              precision={0}
            />

            {showFairPricing ? <span className="text-15">Includes fees</span> : null}
          </div>
        </div>

        <div className="w-177px">
          {!ctasUpdateExperiment || ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_CONTROL_NAME ? (
            <ButtonPrimary isDisabled={adultCounter === 0} onClick={addToCart} classes="h-12">
              Book Now
            </ButtonPrimary>
          ) : (
            <Button
              size="lg"
              onClick={addToCart}
              disabled={adultCounter === 0}
              variant={
                ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME
                  ? 'primary'
                  : 'ultramarine'
              }
            >
              Reserve
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

TimeSlotProductSelector.defaultProps = defaultProps;

export default TimeSlotProductSelector;
